import { useEffect, useLayoutEffect, useMemo } from "react";
import { Transforms, Editor } from 'slate'
import { ReactEditor } from 'nifty-slate-react'

function getDiffStart(prev, next) {
    const length = Math.min(prev.length, next.length)

    for (let i = 0; i < length; i++) {
        if (prev.charAt(i) !== next.charAt(i)) return i
    }

    if (prev.length !== next.length) return length
    return null
}

function getDiffEnd(prev, next, max) {
    const prevLength = prev.length
    const nextLength = next.length
    const length = Math.min(prevLength, nextLength, max)

    for (let i = 0; i < length; i++) {
        const prevChar = prev.charAt(prevLength - i - 1)
        const nextChar = next.charAt(nextLength - i - 1)
        if (prevChar !== nextChar) return i
    }

    if (prev.length !== next.length) return length
    return null
}

function getDiffOffsets(prev, next) {
    if (prev === next) return null
    const start = getDiffStart(prev, next)
    if (start === null) return null
    const maxEnd = Math.min(prev.length - start, next.length - start)
    const end = getDiffEnd(prev, next, maxEnd);
    if (end === null) return null
    return { start, end }
}

function sliceText(text, offsets) {
    return text.slice(offsets.start, text.length - offsets.end)
}

function diffText(prev, next) {
    const offsets = getDiffOffsets(prev, next)
    if (offsets == null) return null
    const insertText = sliceText(next, offsets)
    const removeText = sliceText(prev, offsets)
    return {
        start: offsets.start,
        end: prev.length - offsets.end,
        insertText,
        removeText
    }
}

class CompositionManager {
    constructor(editor) {
        this.editor = editor;
        this.observer = new MutationObserver(this.processMutations);
    }

    connect = () => {
        const rootElement = ReactEditor.toDOMNode(this.editor, this.editor)
        if (this.rootElement === rootElement) return;
        this.rootElement = rootElement;


        const { apply } = this.editor;

        this.editor.apply = op => {
            if (this.bypass || !this.started || (op.type === 'insert_text' && op.text === ' ')) {
                this.bypass = false;
                apply(op);
            }
        };

        this.observer.observe(rootElement, {
            childList: true,
            characterData: true,
            attributes: true,
            subtree: true,
            characterDataOldValue: true
        })
        rootElement.addEventListener("compositionstart", this.onCompositionStart);
        rootElement.addEventListener("compositionend", this.onCompositionEnd);
    }

    disconnect = () => {
        if (this.rootElement) {
            this.rootElement.removeEventListener("compositionstart", this.onCompositionStart);
            this.rootElement.removeEventListener("compositionend", this.onCompositionEnd);
            this.rootElement = null;
            this.observer.disconnect();
        }
    }

    processMutations = (mutations) => {
        const mutation = mutations[0];

        if (mutation.type === 'characterData') {
            const domNode = mutation.target.parentNode;
            const node = ReactEditor.toSlateNode(this.editor, domNode)
            const prevText = node.text;
            let nextText = domNode.textContent;

            // textContent will pad an extra \n when the textContent ends with an \n
            if (nextText.endsWith('\n')) {
                nextText = nextText.slice(0, nextText.length - 1);
            }

            // If the text is no different, there is no diff.
            if (nextText !== prevText) {
                const textDiff = diffText(prevText, nextText)
                if (textDiff !== null) {
                    const path = ReactEditor.findPath(this.editor, node)
                    setTimeout(() => {
                        this.bypass = true;
                        Transforms.insertText(this.editor, textDiff.insertText, {
                            at: {
                                anchor: {path, offset: textDiff.start},
                                focus: {path, offset: textDiff.end}
                            }});
                    }, 30);
                }
            }
        }
    }

    onCompositionStart = (event) => {
        event.preventDefault();
        event.stopPropagation();

        this.started = true;
    }

    onCompositionEnd = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setTimeout(() => {
            this.started = false;
        }, 20);
    }
}  
/**
 * Android Plugin
 *
 * @param {Editor} editor
 */
export function useAndroidPlugin(editor) {
    let compositionManager = useMemo(() => new CompositionManager(editor), [editor]);
    useLayoutEffect(() => {
        compositionManager.connect();
        return compositionManager.disconnect;
    }, [compositionManager]);

    // useEffect(() => {
    // }, []);


    return editor
}
